import { ReactNode, useEffect, useState } from 'react';

import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import MediaQuery from 'react-responsive';
import { useHistory } from 'react-router';

import Tag from 'components/common/Tag';
import { disableScrolling, enableScrolling } from 'components/Header/partials/get_change';
import Render from 'components/render';
import { IS_DEMO, NUMBER_OF_SPOT_PAIRS } from 'constants/constants';
import { MIXPANEL_EVENT_NAMES } from 'constants/mixpanelEventNames';
import routes from 'constants/routes';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import { device } from 'helpers/screenSizes';
import { navigateTo } from 'helpers/url';
import { addBaseURL } from 'helpers/utils';
import { getUserId } from 'selectors/otherSelectors';
import { useAppSelector } from 'storeHooks';
import {
  AngleRightIcon,
  ArrowDown,
  EasyOptionIcon,
  LeaderBoardIcon,
  LineChartBox,
  OptionAnalyticsIcon,
  RoboStrategyIcon,
  Spot,
  StakingPool,
  StrategyBuilder,
} from 'styles/assets/icons';
import Box from 'UIKit/Box';
import DeltaButton from 'UIKit/DeltaButton';

import { getMixpanelPageName } from '../universalSearch/partials/helpers';
import styles from './moreMenu.module.scss';

type MoreMenuHead =
  | 'spot'
  | 'trade'
  | 'invest'
  | 'optionAnalytics'
  | 'optionsSpreads'
  | 'easyOptions'
  | 'roboStrategies'
  | 'staking'
  | 'optionsLeaderBoard'
  | 'strategyBuilder';

type MoreMenuKey = 'label' | 'description';

interface ItemProps {
  id: number;
  label: string;
  href: string;
  description: string;
  key: string;
  contract_type?: string;
  mixpanelLabel: string;
  mixpanelSource?: string;
  icon: ReactNode;
}

const MoreMenuGlobal = () => {
  const { t } = useTranslation(['headerFooter']);
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const userId = useAppSelector(getUserId);
  const history = useHistory();

  const getLocalizedString = (
    str: MoreMenuHead,
    key: MoreMenuKey,
    values?: Record<string, string | number>
  ): string => {
    return t(`headerFooter:header.moreMenu.${str}.${key}` as const, { ...values });
  };

  useEffect(() => {
    if (isDropdownOpen) {
      disableScrolling();
    } else {
      enableScrolling();
    }
  }, [isDropdownOpen]);

  const tradeMenu = [
    {
      id: 1,
      label: getLocalizedString('optionAnalytics', 'label'),
      href: routes.optionsAnalyticsDashboard.default,
      description: getLocalizedString('optionAnalytics', 'description'),
      key: 'optionAnalytics',
      mixpanelLabel: 'Options Analytics - Header',
      icon: <LineChartBox className={styles.icon} />,
    },
    {
      id: 2,
      label: getLocalizedString('easyOptions', 'label'),
      href: routes.easy_options.trade,
      description: getLocalizedString('easyOptions', 'description'),
      key: 'easyOptions',
      mixpanelLabel: 'Options - Header',
      contract_type: 'easy_options',
      icon: <EasyOptionIcon className={styles.icon} />,
    },
    {
      id: 3,
      label: getLocalizedString('spot', 'label'),
      href: routes.spot.trade,
      description: getLocalizedString('spot', 'description', {
        numberOfSpotPairs: NUMBER_OF_SPOT_PAIRS,
      }),
      key: 'spot',
      contract_type: 'spot',
      mixpanelLabel: 'Trade - Header',
      icon: <Spot className={styles.icon} />,
    },
    {
      id: 5,
      label: getLocalizedString('strategyBuilder', 'label'),
      href: addBaseURL(`${routes.options_chain.trade}?activeTab=basketOrders`),
      description: getLocalizedString('strategyBuilder', 'description'),
      key: 'strategyBuilder',
      mixpanelLabel: userId
        ? MIXPANEL_EVENT_NAMES.BASKET_ORDER_SELECTED
        : MIXPANEL_EVENT_NAMES.BASKET_ORDER_MORE_HEADER_SELECTED,
      icon: <StrategyBuilder className={styles.icon} />,
      mixpanelSource: 'more_dropdown',
    },
  ];

  const investMenu = [
    {
      id: 0,
      label: getLocalizedString('roboStrategies', 'label'),
      href: `/invest/robo-strategies`,
      description: getLocalizedString('roboStrategies', 'description'),
      key: 'roboStrategies',
      mixpanelLabel: 'Robo Strategies - Header',
      icon: <RoboStrategyIcon />,
    },
    {
      id: 1,
      label: getLocalizedString('staking', 'label'),
      href: `/invest/staking-pools`,
      description: getLocalizedString('staking', 'description'),
      key: 'staking',
      mixpanelLabel: 'Staking Pools - Header',
      icon: <StakingPool width={28} height={28} />,
    },
  ];
  const leaderBoardMenu = [
    {
      id: 0,
      label: getLocalizedString('optionsLeaderBoard', 'label'),
      href: routes.optionsLeaderboard,
      description: getLocalizedString('optionsLeaderBoard', 'description', {
        length: 50,
      }),
      key: 'optionsLeaderBoard',
      icon: <LeaderBoardIcon className={styles.leaderBoardIcon} />,
      mixpanelLabel: 'Options LeaderBoard Header',
    },
    {
      id: 1,
      label: getLocalizedString('optionAnalytics', 'label'),
      href: routes.optionsAnalyticsDashboard.default,
      description: getLocalizedString('optionAnalytics', 'description', {
        length: 50,
      }),
      key: 'optionAnalytics',
      icon: <OptionAnalyticsIcon className={styles.leaderBoardIcon} />,
      mixpanelLabel: 'Options Analytics - Header',
    },
  ];

  const onItemClick = (
    item: ItemProps,
    menu: 'tradeMenu' | 'investMenu' | 'leaderBoardMenu'
  ) => {
    setDropdownOpen(false);
    const { mixpanelLabel, contract_type: contractType, mixpanelSource, key } = item;
    if (menu === 'tradeMenu') {
      trackMixpanelEvent(mixpanelLabel, {
        contract_type: contractType,
        page_name: getMixpanelPageName(),
        ...(mixpanelSource && { source: mixpanelSource }),
      });
      if (key === 'strategyBuilder') {
        window.location.href = item.href;
        return;
      }
      history.push(item.href);
      return;
    }
    if (menu === 'leaderBoardMenu') {
      history.push(item.href);
      return;
    }

    trackMixpanelEvent(mixpanelLabel, {
      page_name: getMixpanelPageName(),
    });
    navigateTo(item.href, '_blank');
  };

  return (
    <Box
      className={styles.menuContainer}
      onMouseLeave={() => setDropdownOpen(false)}
      onMouseEnter={() => setDropdownOpen(true)}
    >
      <div
        className={cn('flex-set flex--align-items-center', styles.menuHeading, {
          [styles.active]: isDropdownOpen,
        })}
      >
        {t('headerFooter:header.more')}
        <ArrowDown
          className={cn(styles.arrowDownIcon, {
            [styles.active]: isDropdownOpen,
          })}
        />
      </div>
      <Render when={isDropdownOpen}>
        <Box
          className={styles.dropdownMenu}
          onMouseEnter={() => setDropdownOpen(true)}
          onMouseLeave={() => setDropdownOpen(false)}
        >
          <Box className={styles.tradeSection}>
            {t('headerFooter:header.moreMenu.trade')}{' '}
          </Box>
          {tradeMenu.map(item => {
            if (item.key === 'optionAnalytics') {
              return (
                <MediaQuery key={`tradeMenu-${item.key}`} query={device.down.lg}>
                  <DeltaButton
                    data-testid={`tradeMenu-${item.key}`}
                    className={styles.menuBtn}
                    onClick={() => onItemClick(item, 'tradeMenu')}
                    wrapperClassName={styles.menuItem}
                    useBasicButton
                  >
                    {item.icon}
                    <div className={styles.menuItemText}>
                      <div className={styles.heading}>
                        {item.label}{' '}
                        <Tag
                          text={t(`headerFooter:header.notification.new`)}
                          className={styles.newTag}
                        />
                      </div>
                      <div className={styles.description}>{item.description}</div>
                    </div>
                    <AngleRightIcon className={styles.rightArrowIcon} />
                  </DeltaButton>
                </MediaQuery>
              );
            }
            return (
              <DeltaButton
                data-testid={`tradeMenu-${item.key}`}
                key={`tradeMenu-${item.key}`}
                className={styles.menuBtn}
                onClick={() => onItemClick(item, 'tradeMenu')}
                wrapperClassName={styles.menuItem}
                useBasicButton
              >
                {item.icon}
                <div className={styles.menuItemText}>
                  <div className={styles.heading}>{item.label} </div>
                  <div className={styles.description}>{item.description}</div>
                </div>
                <AngleRightIcon className={styles.rightArrowIcon} />
              </DeltaButton>
            );
          })}
          <Box className={styles.investSection}>
            {t('headerFooter:header.moreMenu.invest')}
          </Box>
          {investMenu.map(item => {
            return (
              <DeltaButton
                data-testid={`invest-${item.key}`}
                key={`invest-${item.key}`}
                onClick={() => onItemClick(item, 'investMenu')}
                className={styles.menuBtn}
                wrapperClassName={styles.menuItem}
                useBasicButton
              >
                {item.icon}
                <div className={styles.menuItemText}>
                  <div className={styles.heading}>{item.label}</div>
                  <div className={styles.description}>{item.description}</div>
                </div>
                <AngleRightIcon className={styles.rightArrowIcon} />
              </DeltaButton>
            );
          })}
          <Box renderWhen={!IS_DEMO} className={styles.investSection}>
            {t('headerFooter:header.moreMenu.data')}
          </Box>
          <Render when={!IS_DEMO}>
            {leaderBoardMenu.map(item => {
              return (
                <DeltaButton
                  data-testid={`leaderboard- ${item.key}`}
                  key={`invest-${item.key}`}
                  onClick={() => onItemClick(item, 'leaderBoardMenu')}
                  className={styles.menuBtn}
                  wrapperClassName={styles.menuItem}
                  useBasicButton
                >
                  {item.icon}
                  <div className={styles.menuItemText}>
                    <div className={styles.heading}>
                      {item.label}
                      <Render when={item.tag}>{item.tag}</Render>
                    </div>
                    <div className={styles.description}>{item.description}</div>
                  </div>
                  <AngleRightIcon className={styles.rightArrowIcon} />
                </DeltaButton>
              );
            })}
          </Render>
        </Box>
      </Render>
    </Box>
  );
};
export default MoreMenuGlobal;
